/**
 * Learner-side entry into the Adaptive Learning Engine's media interventions.
 *
 * Authority chain (the client never names a competency, lesson or modality):
 *
 *   engine_decide            -> governed decision (competency, diagnosis,
 *                               objective, modality, policy version)
 *   engine_request_intervention_media(decision_id)
 *                            -> server re-reads the stored decision, applies
 *                               registry-first reuse, kill switches and
 *                               idempotency, and only then may spend
 *   engine_get_intervention_version(request_id)
 *                            -> the EXACT resolved intervention version
 *   engine_record_media_delivery
 *                            -> delivery/exposure telemetry bound to that
 *                               version and the root decision
 *
 * If the engine did not select a media intervention, or generation is pending
 * or unavailable, this hook stays silent and the caller keeps its existing
 * support content. A media failure never strands the learner.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { resolveMediaUrl } from "@/lib/media/signedMediaUrl";

export type AdaptiveMediaModality = "infographic" | "video";

export interface AdaptiveMediaIntervention {
  requestId: string;
  rootDecisionId: string | null;
  interventionVersionId: string;
  version: number;
  lifecycleStatus: string;
  modality: AdaptiveMediaModality;
  title: string | null;
  competencyKey: string | null;
  /** Signed, short-lived URL for the exact delivered version. */
  mediaUrl: string;
  captionPath: string | null;
  objective: string | null;
  diagnosis: string | null;
}

type Status = "idle" | "resolving" | "ready" | "unavailable";

interface JsonRecord { [key: string]: unknown }

function asRecord(value: unknown): JsonRecord {
  return value && typeof value === "object" ? (value as JsonRecord) : {};
}

export function useAdaptiveMediaIntervention(enabled: boolean) {
  const [status, setStatus] = useState<Status>("idle");
  const [intervention, setIntervention] = useState<AdaptiveMediaIntervention | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const startedRef = useRef(false);
  const deliveredRef = useRef(false);

  useEffect(() => {
    if (!enabled || startedRef.current) return;
    startedRef.current = true;
    let cancelled = false;

    const run = async () => {
      setStatus("resolving");
      try {
        // 1. Governed engine decision for the authenticated learner.
        const { data: decision, error: decisionError } = await supabase.rpc("engine_decide", {});
        if (decisionError) throw decisionError;
        const decisionId = asRecord(decision).decision_id as string | undefined;
        if (!decisionId) {
          if (!cancelled) { setReason("no_decision"); setStatus("unavailable"); }
          return;
        }

        // 2. Server-authorized resolve/reuse/generate. The client sends only the
        //    decision id; everything else is read back from the stored decision.
        const { data: resolved, error: resolveError } = await supabase.rpc(
          "engine_request_intervention_media",
          { p_decision_id: decisionId },
        );
        if (resolveError) throw resolveError;
        const res = asRecord(resolved);
        if (res.authorized !== true) {
          if (!cancelled) { setReason(String(res.status ?? "not_authorized")); setStatus("unavailable"); }
          return;
        }

        const requestId = res.request_id as string | undefined;
        if (!requestId) {
          if (!cancelled) { setReason("no_request"); setStatus("unavailable"); }
          return;
        }

        // 3. The exact resolved version — never "latest media for this lesson".
        const { data: versionRow, error: versionError } = await supabase.rpc(
          "engine_get_intervention_version",
          { p_request_id: requestId },
        );
        if (versionError) throw versionError;
        const v = asRecord(versionRow);
        if (v.ready !== true) {
          if (!cancelled) { setReason(String(v.status ?? "pending")); setStatus("unavailable"); }
          return;
        }

        const rawUrl = asRecord(v.media_assets).media_url as string | undefined;
        const signed = rawUrl ? await resolveMediaUrl(rawUrl) : null;
        if (!signed) {
          if (!cancelled) { setReason("no_media"); setStatus("unavailable"); }
          return;
        }

        if (cancelled) return;
        setIntervention({
          requestId,
          rootDecisionId: (v.root_decision_id as string) ?? decisionId,
          interventionVersionId: v.intervention_version_id as string,
          version: Number(v.version ?? 1),
          lifecycleStatus: String(v.lifecycle_status ?? "CALIBRATING"),
          modality: (v.modality as AdaptiveMediaModality) ?? "infographic",
          title: (v.title as string) ?? null,
          competencyKey: (v.competency_key as string) ?? null,
          mediaUrl: signed,
          captionPath: (v.caption_path as string) ?? null,
          objective: (res.objective as string) ?? null,
          diagnosis: (res.diagnosis as string) ?? null,
        });
        setStatus("ready");
      } catch (error) {
        console.warn("Adaptive media intervention unavailable", error);
        if (!cancelled) { setReason("error"); setStatus("unavailable"); }
      }
    };

    void run();
    return () => { cancelled = true; };
  }, [enabled]);

  /** Records delivery + exposure against the exact version. Fires once. */
  const recordDelivery = useCallback(async () => {
    if (!intervention || deliveredRef.current) return;
    deliveredRef.current = true;
    const { error } = await supabase.rpc("engine_record_media_delivery", {
      p_request_id: intervention.requestId,
      p_intervention_version_id: intervention.interventionVersionId,
      p_modality: intervention.modality,
      p_metadata: {
        lifecycle_status: intervention.lifecycleStatus,
        version: intervention.version,
      },
    });
    // Telemetry must never break the learner experience.
    if (error) console.warn("Intervention delivery telemetry failed", error.message);
  }, [intervention]);

  return { status, intervention, reason, recordDelivery };
}
